// 🔴 « Reprendre les travaux de la visite » dans l'éditeur de devis (décision CTO).
// Visible SEULEMENT si le tenant DÉCLARE un `profil_chantier` (capacité, ⛔ aucun
// slug) ET si le dossier a un relevé chantier CLOS. Deux gestes humains :
// « Reprendre… » (lecture, proposition) puis « Ajouter … au devis » (confirmation).
// Logique : ./repriseVisite.js (source unique, testée seule).
import { useEffect, useState } from "react";
import { ClipboardList, Loader2 } from "lucide-react";
import { OhCard, OhCardHeader, OhStatusNote } from "../../../components/ui/oh-charte";
import { profilChantier } from "../../../utils/tenantProfile";
import { afficherDecimal } from "../../../utils/devisEtapes";
import {
  appliquerReprise, chargerProposition, releveClosLePlusRecent,
} from "./repriseVisite";

const boutonPrimaire = {
  background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)",
  border: "0.5px solid var(--oh-accent-solid)", borderRadius: "var(--oh-radius-control)",
  height: 36, padding: "0 var(--oh-space-4)", fontWeight: 500, fontSize: 14,
};
const boutonSecondaire = {
  background: "transparent", color: "var(--oh-accent-text)",
  border: "0.5px solid var(--oh-accent)", borderRadius: "var(--oh-radius-control)",
  height: 36, padding: "0 var(--oh-space-4)", fontWeight: 500, fontSize: 14,
};

const libellePoste = (poste) =>
  [poste.designation, poste.quantite != null ? `${afficherDecimal(poste.quantite)} ${poste.unite || ""}`.trim() : null]
    .filter(Boolean).join(" — ");

export default function RepriseVisiteDevis({
  api, dossierId, devisId, modifiable, entetes, entetesJson, extraLigne, onDevisMisAJour,
  fetchFn = fetch,
}) {
  const actif = Boolean(profilChantier()) && Boolean(dossierId) && Boolean(devisId) && modifiable;
  const [releveClos, setReleveClos] = useState(null);
  const [proposition, setProposition] = useState(null);
  const [resultat, setResultat] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState(null);

  // Lecture de la LISTE des relevés : le bouton n'existe que s'il y a un relevé clos.
  useEffect(() => {
    if (!actif) return undefined;
    let vivant = true;
    fetchFn(`${api}/api/chantier/sessions?dossier_id=${encodeURIComponent(dossierId)}`,
            { headers: entetes, credentials: "include" })
      .then((r) => (r.ok ? r.json() : { releves: [] }))
      .then((d) => { if (vivant) setReleveClos(releveClosLePlusRecent(d.releves)); })
      .catch(() => { if (vivant) setReleveClos(null); });
    return () => { vivant = false; };
  }, [actif, api, dossierId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!actif || !releveClos) return null;

  const proposer = async () => {
    setOccupe(true); setErreur(null); setResultat(null);
    try {
      setProposition(await chargerProposition({ api, dossierId, entetes, fetchFn }));
    } catch (e) {
      setErreur("Lecture du relevé de visite impossible");
    } finally { setOccupe(false); }
  };

  const confirmer = async () => {
    setOccupe(true); setErreur(null);
    try {
      const r = await appliquerReprise({ api, devisId, entetesJson, repris: proposition.repris,
                                         extraLigne, fetchFn });
      if (r.devis) onDevisMisAJour?.(r.devis);
      setResultat({ ajoutes: r.ajoutes, nonRepris: [...proposition.nonRepris, ...r.refuses] });
      setProposition(null);
    } catch (e) {
      setErreur("Ajout des lignes interrompu");
    } finally { setOccupe(false); }
  };

  const nonReprisAffiches = resultat ? resultat.nonRepris : proposition?.nonRepris || [];

  return (
    <OhCard data-testid="reprise-visite" style={{ marginBottom: "var(--oh-space-6)" }}>
      <OhCardHeader
        title="Travaux relevés pendant la visite"
        description="Les postes reliés au catalogue deviennent des lignes au prix du catalogue, après votre confirmation."
        // ⛔ Plus de bouton après une reprise : un second clic DUPLIQUERAIT les lignes.
        right={!proposition && !resultat && (
          <button type="button" style={boutonSecondaire} onClick={proposer} disabled={occupe}
                  data-testid="reprise-visite-proposer">
            {occupe ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <ClipboardList className="w-4 h-4 inline" />}
            {" "}Reprendre les travaux de la visite
          </button>
        )}
      />
      {erreur && <OhStatusNote variant="error">{erreur}</OhStatusNote>}

      {proposition && (
        <div data-testid="reprise-visite-proposition" style={{ display: "grid", gap: "var(--oh-space-3)" }}>
          <div style={{ fontWeight: 500, color: "var(--oh-text)" }}>
            {proposition.repris.length} poste(s) à ajouter au devis
          </div>
          <ul style={{ display: "grid", gap: 4, color: "var(--oh-text)" }}>
            {proposition.repris.map(({ poste, produit }) => (
              <li key={poste.id} data-testid="reprise-visite-repris">
                {libellePoste(poste)}
                <span style={{ color: "var(--oh-text-secondary)" }}>
                  {" "}⇒ {produit.designation} ({produit.reference}{produit.unite ? `, unité catalogue : ${produit.unite}` : ""})
                </span>
              </li>
            ))}
          </ul>
          <div style={{ display: "flex", gap: "var(--oh-space-2)" }}>
            <button type="button" style={boutonPrimaire} onClick={confirmer}
                    disabled={occupe || proposition.repris.length === 0}
                    data-testid="reprise-visite-confirmer">
              Ajouter {proposition.repris.length} ligne(s) au devis
            </button>
            <button type="button" style={boutonSecondaire} onClick={() => setProposition(null)} disabled={occupe}
                    data-testid="reprise-visite-annuler">
              Annuler
            </button>
          </div>
        </div>
      )}

      {resultat && (
        <OhStatusNote variant="success" data-testid="reprise-visite-resultat">
          {resultat.ajoutes.length} ligne(s) ajoutée(s) au devis, au prix du catalogue.
        </OhStatusNote>
      )}

      {nonReprisAffiches.length > 0 && (
        <div data-testid="reprise-visite-non-repris" style={{ marginTop: "var(--oh-space-3)" }}>
          <OhStatusNote variant="info" title={`${nonReprisAffiches.length} poste(s) non repris`}>
            <ul>
              {nonReprisAffiches.map(({ poste, motif }, i) => (
                <li key={poste.id || i} data-testid="reprise-visite-non-repris-poste">
                  {libellePoste(poste)} : {motif}
                </li>
              ))}
            </ul>
          </OhStatusNote>
        </div>
      )}
    </OhCard>
  );
}
